import { Source, Layer } from 'react-map-gl/maplibre'
import type { CircleLayerSpecification, FilterSpecification } from 'maplibre-gl'
import { reverse } from 'lodash-es'
import { DIRECTIONAL_ATTRIBUTES, PMTileSources, VisibleLayers } from '~/constants'
import { useAppStore, usePMTiles } from '~/hooks'
import { colorBreaksForAttributeAndDepths, isEnhancedDivergingMetric } from '~/util/waves'

const DEFAULT_RADIUS = 5

// Define zoom breaks with min and max radius values at each zoom level
const ZOOM_BREAKS = [
  { zoom: 5, minRadius: 1, maxRadius: 2.5 },
  { zoom: 10, minRadius: 4, maxRadius: 10 },
  { zoom: 15, minRadius: 6, maxRadius: 15 },
  { zoom: 20, minRadius: 8, maxRadius: 20 },
] as const

// Enhanced diverging metrics get 2x max radius (same min) to emphasize extreme values
const ZOOM_BREAKS_ENHANCED = [
  { zoom: 5, minRadius: 1, maxRadius: 5 },
  { zoom: 10, minRadius: 3, maxRadius: 20 },
  { zoom: 15, minRadius: 4, maxRadius: 30 },
  { zoom: 20, minRadius: 5, maxRadius: 40 },
] as const

export default function WavePointsLayer() {
  const { sources, layerInfo } = usePMTiles()
  const { layerVisibility, dataset, dynamicWavesProperty, wavesProperty, depths } = useAppStore()

  const source = PMTileSources.WAVES
  const url = sources[PMTileSources.WAVES]

  const wavesPropertyToUse = dynamicWavesProperty || wavesProperty
  const shouldShowWavePoints =
    dataset === 'waves' &&
    layerVisibility[VisibleLayers.WAVES] &&
    (wavesProperty !== 'hs_change' || Boolean(dynamicWavesProperty))

  const wavesLayer = layerInfo?.[PMTileSources.WAVES]

  const colorBreaks = colorBreaksForAttributeAndDepths(depths, wavesPropertyToUse, wavesLayer!)

  // Color breaks are stored in descending order for the legend, but MapLibre needs ascending order
  const sortedBreaks = colorBreaks ? reverse([...colorBreaks]) : []

  const isEnhancedDiverging = isEnhancedDivergingMetric(wavesPropertyToUse)

  // Create radius breaks for a given zoom level
  // For enhanced diverging metrics: V-shaped (0 = small, extremes = large)
  // For all other metrics: linear (low = small, high = large)
  const createRadiusBreaks = (minRadius: number, maxRadius: number) => {
    if (isEnhancedDiverging) {
      const centerIndex = Math.floor(sortedBreaks.length / 2)
      return sortedBreaks.map(([breakValue], index) => {
        // Distance from center (0-based), normalized to 0-1
        const distanceFromCenter = Math.abs(index - centerIndex) / centerIndex
        const radius = minRadius + (maxRadius - minRadius) * distanceFromCenter
        return [breakValue, radius]
      })
    }
    // Regular linear mapping
    return sortedBreaks.map(([breakValue], index) => {
      const t = index / (sortedBreaks.length - 1)
      const radius = minRadius + (maxRadius - minRadius) * t
      return [breakValue, radius]
    })
  }

  // Create data interpolation expression for a given zoom level
  const createRadiusExpressionForZoom = (minRadius: number, maxRadius: number) => {
    const radiusBreaks = createRadiusBreaks(minRadius, maxRadius)
    return [
      'interpolate',
      ['linear'],
      ['get', wavesPropertyToUse],
      ...radiusBreaks.flatMap(([breakValue, radius]) => [breakValue, radius]),
    ]
  }

  const noBreaks = !colorBreaks || colorBreaks.length === 0 || !wavesPropertyToUse

  const colorExpression = noBreaks
    ? ('red' as never)
    : ([
        'interpolate',
        ['linear'],
        ['get', wavesPropertyToUse],
        ...sortedBreaks.flatMap(([breakValue, color]) => [breakValue, color]),
      ] as never)

  // Build radius expression with zoom interpolation at the top level
  const zoomBreaks = isEnhancedDiverging ? ZOOM_BREAKS_ENHANCED : ZOOM_BREAKS
  const radiusExpression = noBreaks
    ? DEFAULT_RADIUS
    : ([
        'interpolate',
        ['linear'],
        ['zoom'],
        ...zoomBreaks.flatMap(({ zoom, minRadius, maxRadius }) => [
          zoom,
          dynamicWavesProperty && !isEnhancedDiverging
            ? (minRadius + maxRadius) / 2
            : createRadiusExpressionForZoom(minRadius, maxRadius),
        ]),
      ] as never)

  const config: CircleLayerSpecification = {
    source,
    id: source,
    paint: {
      'circle-color': colorExpression,
      // If we are displaying a directional attribute then it is important that
      // the circles don't actually show up (because we render arrows instead).
      // However, it's *also* important that this layer is still *visible*,
      // since the map tooltip queries the features rendered in this layer to
      // know what to display.
      'circle-radius': DIRECTIONAL_ATTRIBUTES.includes(wavesProperty) ? 0 : radiusExpression,
    },
    type: 'circle',
    layout: {
      visibility: shouldShowWavePoints ? 'visible' : 'none',
    },
    ...(dataset === 'waves' && {
      filter: (depths.length > 0
        ? ['in', ['get', 'point_depth'], ['literal', depths]]
        : ['==', ['get', 'point_depth'], -1]) as FilterSpecification, // No match when empty - hides all points
    }),
  }

  return (
    <Source key={source} id={source} type="vector" url={`pmtiles://${url}`}>
      {layerInfo![source].map((l) => (
        <Layer key={l.layer} source-layer={l.layer} {...config} id={l.layer} />
      ))}
    </Source>
  )
}
